import { useMemo } from 'react';
import { handleError } from '../gql/config/URQLProvider';
import { Location, useGetOrganisationHierarchyQuery } from '../gql/graphql.generated';
import { buildDivisionNode, flattenTree, LocationNode, TreeNode } from '../hierachy/organisationHierarchy.utils';
import { useAllLocations } from '../useLocations';

export type DivisionGroup = {
  id: string;
  name: string;
  locations: Location[];
};

export const useLocationGroups = (): DivisionGroup[] => {
  const locations = useAllLocations();
  const [{ data, error }] = useGetOrganisationHierarchyQuery();

  if (error) {
    handleError(error);
  }

  const hierarchy = data?.getOrganisationHierarchy;

  return useMemo<DivisionGroup[]>(() => {
    if (!hierarchy || !locations.length) {
      return [];
    }

    const rootNode = buildDivisionNode(hierarchy);
    const nodeMap = flattenTree(rootNode, new Map<string, TreeNode>());

    const divisionMap = new Map<string, DivisionGroup>();

    for (const node of nodeMap.values()) {
      if (node.type === 'division') {
        divisionMap.set(node.id, {
          id: node.id,
          name: node.name,
          locations: []
        });
      }
    }

    for (const location of locations) {
      const locationNode = Array.from(nodeMap.values()).find(
        (node): node is LocationNode => node.type === 'location' && node.locationId === location.id
      );

      if (!locationNode?.parentId || !divisionMap.has(locationNode.parentId)) {
        continue;
      }

      divisionMap.get(locationNode.parentId)!.locations.push(location);
    }

    return Array.from(divisionMap.values()).filter(group => group.locations.length > 0);
  }, [hierarchy, locations]);
};
